/* Choreographed fountains, reflected water and the Luxor light animate the Vegas drawing.
   Merchant artwork stays untouched: these coordinates belong only to the bundled print. */
if (!customElements.get('skyline-scene')) customElements.define('skyline-scene', class extends HTMLElement {
  connectedCallback() {
    if (this.abort && !this.abort.signal.aborted) return;
    this.abort = new AbortController();
    this.image = this.querySelector('[data-skyline-image]');
    this.canvas = this.querySelector('[data-skyline-canvas]');
    this.button = this.querySelector('[data-skyline-motion]');
    if (!this.hasAttribute('data-skyline-fountains') || !this.image || !this.canvas) return;
    try { this.context = this.canvas.getContext('2d'); } catch { this.context = null; }
    if (!this.context || typeof requestAnimationFrame !== 'function') return;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.inView = typeof IntersectionObserver !== 'function';
    this.paused ??= false;
    this.phase ??= 0;
    const on = (node, type, callback) => node.addEventListener(type, callback, {signal:this.abort.signal});
    on(this.image, 'load', () => this.refresh());
    if (this.button) on(this.button, 'click', () => { this.paused = !this.paused; this.refresh(); });
    on(document, 'visibilitychange', () => this.refresh());
    on(this.reduced, 'change', () => this.refresh());
    if (typeof IntersectionObserver === 'function') {
      this.observer = new IntersectionObserver(entries => { this.inView = entries.some(e => e.isIntersecting); this.refresh(); });
      this.observer.observe(this.canvas);
    }
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.image);
    }
    this.refresh();
  }
  refresh() {
    this.stop();
    const ready = this.image.complete && this.image.naturalWidth > 0;
    this.canvas.hidden = !ready || this.reduced.matches;
    if (this.button) {
      this.button.hidden = this.canvas.hidden;
      this.button.textContent = this.paused ? 'Play scene' : 'Pause scene';
      this.button.setAttribute('aria-label', this.paused ? 'Play fountain animation' : 'Pause fountain animation');
    }
    if (!ready || this.reduced.matches) return;
    this.paint();
    if (this.paused || !this.inView || document.hidden) return;
    this.lastTime = null;
    const tick = time => {
      if (this.abort.signal.aborted || this.paused || this.reduced.matches || !this.inView || document.hidden) { this.stop(); return; }
      if (this.lastTime === null || time - this.lastTime >= 32) {
        this.phase += this.lastTime === null ? 0 : Math.min(time - this.lastTime,80) / 1000;
        this.lastTime = time;
        this.paint();
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }
  paint() {
    const box = this.image.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const ratio = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1,1.5);
    const width = Math.min(1600,Math.round(box.width * ratio));
    const height = Math.round(width * 9 / 16);
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    const ctx = this.context;
    ctx.clearRect(0,0,width,height);
    const sx = width / 1600, sy = height / 900;
    // The light originates at the pyramid tip in the bundled drawing.
    // All brightness changes take several seconds; the buildings stay still.
    const beamTop = 434 + Math.sin(this.phase * .16) * 78;
    const beam = ctx.createLinearGradient(434*sx,472*sy,beamTop*sx,80*sy);
    beam.addColorStop(0,'rgba(255,225,150,.28)');
    beam.addColorStop(.45,'rgba(232,187,80,.055)');
    beam.addColorStop(1,'rgba(232,187,80,0)');
    ctx.fillStyle = beam;
    ctx.beginPath();ctx.moveTo(429*sx,474*sy);ctx.lineTo((beamTop-30)*sx,65*sy);ctx.lineTo((beamTop+30)*sx,65*sy);ctx.lineTo(439*sx,474*sy);ctx.closePath();ctx.fill();
    // Quadratic trajectories follow the print's fountains in 1600×900 space.
    const jets = [[440,730,460,610,505,730],[495,730,526,568,576,730],[554,730,586,610,633,730],
      [613,730,651,556,700,730],[680,730,716,606,762,730],[738,730,778,572,825,730],
      [804,730,847,618,891,730],[863,730,905,600,951,730]];
    for (let j=0;j<jets.length;j++) {
      const [x0,y0,x1,peak,x2,y2] = jets[j];
      const lift = .78 + .22 * Math.sin(this.phase * .7 - j * .5);
      const y1 = y0 - (y0-peak) * lift;
      ctx.strokeStyle = '#f9d98c';ctx.lineWidth = Math.max(.7,1.2*sx);
      ctx.globalAlpha = .3 + .14*lift;
      ctx.beginPath();ctx.moveTo(x0*sx,y0*sy);ctx.quadraticCurveTo(x1*sx,y1*sy,x2*sx,y2*sy);ctx.stroke();
      ctx.fillStyle = '#fff0bd';
      for (let d=0;d<14;d++) {
        const t = (this.phase * .32 + j * .071 + d / 14) % 1;
        const x = (1-t)**2*x0 + 2*(1-t)*t*x1 + t*t*x2;
        const y = (1-t)**2*y0 + 2*(1-t)*t*y1 + t*t*y2;
        ctx.globalAlpha = .8 * Math.sin(t * Math.PI);
        ctx.beginPath();ctx.arc(x*sx,y*sy,Math.max(.65,1.3*sx),0,Math.PI*2);ctx.fill();
      }
    }
    // Moving horizontal reflections stay within the water below the skyline.
    ctx.strokeStyle = '#efc96a';ctx.lineWidth = Math.max(.55,sx);
    for (let row=0;row<18;row++) {
      const y = 738 + row*3.2;
      for (let col=0;col<12;col++) {
        const x = 140 + col*108 + Math.sin(this.phase*.65+row*.8+col)*12;
        const length = 9 + (1+Math.sin(this.phase*.9-col*.6+row))*16;
        ctx.globalAlpha = (.13+.11*Math.sin(this.phase*.6+col+row))*(1-row/21);
        ctx.beginPath();ctx.moveTo(x*sx,y*sy);ctx.lineTo((x+length)*sx,y*sy);ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }
  stop() { if (this.frame !== undefined) cancelAnimationFrame(this.frame); this.frame = undefined; }
  disconnectedCallback() {
    this.abort?.abort(); this.stop(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
    if (this.canvas) this.canvas.hidden = true;
    if (this.button) this.button.hidden = true;
  }
});
